// Admin-only 24-hour PEG (gastrostomy) summary per patient.
// Aggregates all feedings + tube care from all shifts of the selected date.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft, Loader2, Utensils, AlertTriangle, CheckCircle2, Calendar, User2, Ruler,
} from "lucide-react";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { TOLERANCE_LABELS, STOMA_LABELS, PATENCY_LABELS } from "@/lib/gastrostomy-alerts";

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export default function GastrostomySummaryPage() {
  const nav = useNavigate();
  const [patients, setPatients] = useState(null);
  const [selectedId, setSelectedId] = useState("");
  const [date, setDate] = useState(todayIso());
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/patients");
        const peg = (Array.isArray(data) ? data : []).filter((p) => p.gastrostomy_active);
        setPatients(peg);
        if (peg.length && !selectedId) setSelectedId(peg[0].id);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Error cargando pacientes");
        setPatients([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSummary = useCallback(async () => {
    if (!selectedId || !date) {
      setSummary(null);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(`/patients/${selectedId}/gastrostomy-summary`, {
        params: { date },
      });
      setSummary(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error cargando resumen");
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [selectedId, date]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const selectedPatient = useMemo(
    () => (patients || []).find((p) => p.id === selectedId),
    [patients, selectedId]
  );

  const hasIssues =
    (summary?.intolerance_count || 0) > 0 ||
    (summary?.high_residual_count || 0) > 0 ||
    (summary?.patency_issues || []).length > 0 ||
    (summary?.stoma_findings || []).length > 0;

  return (
    <div className="min-h-screen bg-[#F1F5F9]">
      <AppHeader subtitle="Resumen 24h - Gastrostomia (PEG)" />

      <main className="mx-auto max-w-3xl px-4 py-6">
        <Button
          variant="ghost"
          className="mb-3 h-10 px-2 text-slate-600"
          onClick={() => nav("/dashboard")}
          data-testid="peg-back-btn"
        >
          <ArrowLeft className="mr-1 h-4 w-4" /> Volver
        </Button>

        <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <h1 className="text-lg font-bold tracking-tight text-slate-900 sm:text-xl">
            Balance 24h de Alimentacion por Sonda
          </h1>
          <p className="mt-0.5 text-xs text-slate-500">
            Agrupa todas las tomas y cuidados de la sonda registrados por cualquier enfermera para este paciente en el dia seleccionado.
          </p>
        </div>

        {patients === null && (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          </div>
        )}

        {patients && patients.length === 0 && (
          <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white px-6 py-10 text-center">
            <Utensils className="mx-auto mb-2 h-8 w-8 text-slate-300" />
            <p className="font-semibold text-slate-700">Sin pacientes con PEG</p>
            <p className="mt-1 text-sm text-slate-500">
              Activa la Gastrostomia (PEG) en la ficha del paciente para que aparezca aqui.
            </p>
          </div>
        )}

        {patients && patients.length > 0 && (
          <>
            <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label className="mb-1 flex items-center gap-1 text-xs font-bold uppercase text-slate-500">
                  <User2 className="h-3 w-3" /> Paciente
                </Label>
                <Select value={selectedId} onValueChange={setSelectedId}>
                  <SelectTrigger className="h-11 text-base" data-testid="peg-summary-patient">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {patients.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1 flex items-center gap-1 text-xs font-bold uppercase text-slate-500">
                  <Calendar className="h-3 w-3" /> Fecha
                </Label>
                <Input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="h-11 text-base"
                  data-testid="peg-summary-date"
                />
              </div>
            </div>

            {loading && (
              <div className="flex justify-center py-10">
                <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
              </div>
            )}

            {!loading && summary && (
              <div className="space-y-4">
                {/* Totals card */}
                <div
                  className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                  data-testid="peg-summary-totals"
                >
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        {selectedPatient?.name} - {summary.date}
                      </p>
                      <p className="mt-1 text-lg font-bold text-slate-900">
                        {summary.feedings_count} tomas · {summary.per_shift.length} turnos
                      </p>
                    </div>
                    <div
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${
                        !summary.feedings_count
                          ? "bg-slate-100 text-slate-600"
                          : hasIssues
                          ? "bg-red-100 text-red-800"
                          : "bg-emerald-100 text-emerald-800"
                      }`}
                      data-testid="peg-summary-badge"
                    >
                      {!summary.feedings_count ? (
                        "Sin datos"
                      ) : hasIssues ? (
                        <>
                          <AlertTriangle className="h-3.5 w-3.5" /> Con hallazgos
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="h-3.5 w-3.5" /> Sin incidencias
                        </>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    <div className="rounded-xl bg-slate-50 p-3">
                      <div className="text-[10px] font-bold uppercase text-slate-500">Peso</div>
                      <div className="mt-1 font-mono text-lg font-bold text-slate-900">
                        {summary.weight_kg || "--"} <span className="text-xs font-medium">kg</span>
                      </div>
                    </div>
                    <div className="rounded-xl bg-amber-50 p-3">
                      <div className="text-[10px] font-bold uppercase text-amber-700">Formula</div>
                      <div className="mt-1 font-mono text-lg font-bold text-amber-900">
                        {summary.total_formula_ml} <span className="text-xs font-medium">ml</span>
                      </div>
                    </div>
                    <div className="rounded-xl bg-cyan-50 p-3">
                      <div className="text-[10px] font-bold uppercase text-cyan-700">Agua (lavados)</div>
                      <div className="mt-1 font-mono text-lg font-bold text-cyan-900">
                        {summary.total_water_ml} <span className="text-xs font-medium">ml</span>
                      </div>
                    </div>
                    <div className="rounded-xl bg-blue-50 p-3">
                      <div className="text-[10px] font-bold uppercase text-blue-700">Total enteral</div>
                      <div
                        className="mt-1 font-mono text-lg font-bold text-blue-900"
                        data-testid="peg-summary-total"
                      >
                        {summary.total_enteral_ml} <span className="text-xs font-medium">ml</span>
                      </div>
                    </div>
                  </div>

                  {(summary.intolerance_count > 0 || summary.high_residual_count > 0) && (
                    <div
                      className="mt-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-2 text-xs font-medium text-red-800"
                      data-testid="peg-summary-alert"
                    >
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                      <span>
                        {summary.intolerance_count > 0 && (
                          <>{summary.intolerance_count} toma(s) con intolerancia. </>
                        )}
                        {summary.high_residual_count > 0 && (
                          <>{summary.high_residual_count} residuo(s) gastrico(s) &gt;200 ml. </>
                        )}
                        Valorar tolerancia y notificar al medico tratante.
                      </span>
                    </div>
                  )}
                </div>

                {/* External length trend */}
                {(summary.external_lengths || []).length > 0 && (
                  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500">
                      <Ruler className="h-3.5 w-3.5" /> Marca externa de la sonda
                    </p>
                    <ul className="divide-y divide-slate-100">
                      {summary.external_lengths.map((e, i) => (
                        <li key={i} className="flex items-center justify-between py-2 text-sm">
                          <span className="text-slate-700">
                            {e.shift_start || "--:--"} · {e.owner_name}
                          </span>
                          <span className="font-mono font-bold text-slate-900">
                            {e.external_length_cm} cm
                          </span>
                        </li>
                      ))}
                    </ul>
                    <p className="mt-1 text-[11px] italic text-slate-500">
                      Un cambio en la marca externa puede indicar migracion de la sonda.
                    </p>
                  </div>
                )}

                {/* Per-shift breakdown */}
                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">
                    Detalle por turno
                  </p>
                  {summary.per_shift.length === 0 ? (
                    <p className="py-4 text-center text-sm text-slate-500">
                      Sin tomas registradas en este dia.
                    </p>
                  ) : (
                    <ul className="divide-y divide-slate-100">
                      {summary.per_shift.map((s, i) => (
                        <li key={i} className="flex items-center justify-between gap-3 py-2 text-sm">
                          <div>
                            <div className="font-semibold text-slate-900">
                              {s.shift_start || "--:--"} · {s.owner_name}
                            </div>
                            <div className="text-xs text-slate-500">
                              {s.feedings_count} toma(s)
                              {s.intolerance_count > 0 && (
                                <span className="ml-1 font-bold text-red-700">
                                  · {s.intolerance_count} intolerancia(s)
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-right font-mono">
                            <div className="text-sm text-slate-700">
                              {s.formula_ml} ml formula
                            </div>
                            <div className="text-xs font-bold text-cyan-700">
                              + {s.water_ml} ml agua
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Intolerance events */}
                {(summary.intolerance_events || []).length > 0 && (
                  <div className="rounded-2xl border border-red-200 bg-red-50 p-4 shadow-sm">
                    <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-red-800">
                      <AlertTriangle className="h-3.5 w-3.5" /> Tomas con intolerancia
                    </p>
                    <ul className="space-y-1 text-sm text-red-900">
                      {summary.intolerance_events.map((e, i) => (
                        <li key={i}>
                          <span className="font-mono font-bold">
                            #{e.number || "?"} · {e.time || "--:--"}
                          </span>{" "}
                          — {TOLERANCE_LABELS[e.tolerance] || e.tolerance}
                          {e.formula_type && (
                            <span className="text-xs text-red-700"> ({e.formula_type})</span>
                          )}
                          {e.notes && <span className="ml-1 italic text-red-700">— {e.notes}</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Tube / stoma findings */}
                {((summary.patency_issues || []).length > 0 ||
                  (summary.stoma_findings || []).length > 0) && (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 shadow-sm">
                    <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-amber-800">
                      <AlertTriangle className="h-3.5 w-3.5" /> Hallazgos de sonda / estoma
                    </p>
                    <ul className="space-y-1 text-sm text-amber-900">
                      {(summary.patency_issues || []).map((e, i) => (
                        <li key={`p-${i}`}>
                          <span className="font-mono font-bold">{e.shift_start || "--:--"}</span> ·{" "}
                          {e.owner_name} — Permeabilidad:{" "}
                          <strong>{PATENCY_LABELS[e.patency] || e.patency}</strong>
                        </li>
                      ))}
                      {(summary.stoma_findings || []).map((e, i) => (
                        <li key={`s-${i}`}>
                          <span className="font-mono font-bold">{e.shift_start || "--:--"}</span> ·{" "}
                          {e.owner_name} — Estoma:{" "}
                          {(e.findings || [])
                            .map((f) => STOMA_LABELS[f] || f)
                            .join(", ")}
                          {e.notes && <span className="ml-1 italic">— {e.notes}</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
